import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css'; // fullscreen photo viewer
import LoadErrorScreen from './LoadErrorScreen';
import CardTitleRow from './CardTitleRow';

const API = process.env.REACT_APP_SERVER_URL;

const bookButtonSx = {
  textTransform: 'none',
  backgroundColor: '#2e7d32',
  color: '#fff',
  '&:hover': { backgroundColor: '#1b5e20' },
  '&.Mui-disabled': {
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
  },
};

const getPhotoUrl = (photo) => {
  if (!photo || typeof photo !== 'string') return null;
  const t = photo.trim();
  if (!t) return null;
  if (t.startsWith('http://') || t.startsWith('https://')) return t;
  if (t.startsWith('/')) return `${API}${t}`;
  return `${API}/${t}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('ru-RU');
  } catch {
    return '—';
  }
};

function getPassportSeries(pd) {
  return String(pd?.passportSeries ?? pd?.series ?? '').trim();
}

function getPassportNumber(pd) {
  return String(pd?.passportNumber ?? pd?.number ?? '').trim();
}

function profileGapsFromUser(user) {
  if (!user) return ['fullName', 'passportSeries', 'passportNumber', 'phoneNumber'];
  const pd = user.passportData || {};
  const gaps = [];
  if (!String(pd.fullName || '').trim()) gaps.push('fullName');
  if (!getPassportSeries(pd)) gaps.push('passportSeries');
  if (!getPassportNumber(pd)) gaps.push('passportNumber');
  if (!String(user.phoneNumber || '').trim()) gaps.push('phoneNumber');
  return gaps;
}

const TourPage = () => {
  const { tourNumber } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);

  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [pendingPeriodIndex, setPendingPeriodIndex] = useState(null);
  const [profileDraft, setProfileDraft] = useState({
    fullName: '',
    passportSeries: '',
    passportNumber: '',
    phoneNumber: '',
  });
  const [profileGaps, setProfileGaps] = useState([]);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const departuresRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    setNotFound(false);
    setError('');
    fetch(`${API}/api/tours/public/${encodeURIComponent(tourNumber || '')}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (res.status === 404) {
          setNotFound(true);
          return null;
        }
        if (!res.ok) throw new Error(data.message || 'Не удалось загрузить тур');
        return data;
      })
      .then((data) => {
        if (!cancelled && data) setTour(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setLoadError(true);
          setTour(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tourNumber]);

  const photos = useMemo(() => {
    const list = Array.isArray(tour?.photos) ? tour.photos : [];
    return list.map(getPhotoUrl).filter(Boolean);
  }, [tour?.photos]);

  const lightboxSlides = useMemo(
    () => photos.map((src) => ({ src })),
    [photos]
  );

  const openViewer = (idx) => {
    setActivePhotoIndex(idx);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
  };

  const scrollToDepartures = () => {
    departuresRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goBook = (periodIndex) => {
    navigate(`/tours/${encodeURIComponent(tourNumber)}/book?period=${periodIndex}`);
  };

  const handleBookClick = async (periodIndex) => {
    const seats = tour?.periods?.[periodIndex]?.seatsAvailable ?? 0;
    if (seats < 1) return;

    const token = localStorage.getItem('token');
    if (!token) {
      setPendingPeriodIndex(periodIndex);
      setAuthDialogOpen(true);
      return;
    }

    try {
      const res = await fetch(`${API}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const user = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(user.message || 'Не удалось загрузить профиль');

      const gaps = profileGapsFromUser(user);
      if (gaps.length) {
        const pd = user.passportData || {};
        setProfileDraft({
          fullName: String(pd.fullName || '').trim(),
          passportSeries: getPassportSeries(pd),
          passportNumber: getPassportNumber(pd),
          phoneNumber: String(user.phoneNumber || '').trim(),
        });
        setProfileGaps(gaps);
        setPendingPeriodIndex(periodIndex);
        setProfileError('');
        setProfileDialogOpen(true);
        return;
      }

      goBook(periodIndex);
    } catch (e) {
      setError(e.message || 'Ошибка проверки профиля');
    }
  };

  const handleSaveProfileAndBook = async () => {
    const token = localStorage.getItem('token');
    if (!token || pendingPeriodIndex == null) return;
    setProfileError('');
    setProfileSaving(true);
    try {
      const phoneNorm = profileDraft.phoneNumber.replace(/\s/g, '');
      const resProfile = await fetch(`${API}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const current = await resProfile.json().catch(() => ({}));
      if (!resProfile.ok) throw new Error(current.message || 'Профиль');

      const prevPd = current.passportData || {};
      const passportData = {
        ...prevPd,
        fullName: profileDraft.fullName.trim(),
        passportSeries: profileDraft.passportSeries.trim(),
        passportNumber: profileDraft.passportNumber.trim(),
      };

      const res = await fetch(`${API}/api/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          phoneNumber: phoneNorm,
          passportData,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Не удалось сохранить');

      const nextUser = data.user || { ...current, phoneNumber: phoneNorm, passportData };
      const gaps = profileGapsFromUser(nextUser);
      if (gaps.length) {
        setProfileGaps(gaps);
        throw new Error('Заполните все обязательные поля.');
      }

      setProfileDialogOpen(false);
      const idx = pendingPeriodIndex;
      setPendingPeriodIndex(null);
      goBook(idx);
    } catch (e) {
      setProfileError(e.message || 'Ошибка');
    } finally {
      setProfileSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '320px' }}>
        <CircularProgress sx={{ color: '#8B7D6B' }} />
      </Box>
    );
  }

  if (loadError) {
    return <LoadErrorScreen />;
  }

  if (!tour) {
    return (
      <Container maxWidth="md" sx={{ mt: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {notFound ? 'Тур не найден.' : (error || 'Тур не найден.')}
        </Alert>
        <Button component={Link} to="/tours" startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none' }}>
          К списку туров
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 3 }}>
      <Button component={Link} to="/tours" startIcon={<ArrowBackIcon />} sx={{ mb: 2, textTransform: 'none' }}>
        К списку туров
      </Button>

      <CardTitleRow
        type="tour"
        entityId={tour._id}
        titleVariant="h4"
        titleSx={{ fontWeight: 700, mb: 0.5 }}
      >
        {tour.title}
      </CardTitleRow>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {tour.tourNumber} {tour.destination ? `• ${tour.destination}` : ''}
      </Typography>

      {photos.length > 0 && (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {photos.slice(0, 6).map((url, idx) => (
            <Grid item xs={6} sm={4} md={2} key={`${url}-${idx}`}>
              <Card sx={{ borderRadius: 2 }}>
                <CardMedia
                  component="img"
                  image={url}
                  alt={`Фото тура ${idx + 1}`}
                  sx={{ height: 120, objectFit: 'cover', cursor: 'pointer' }}
                  onClick={() => openViewer(idx)}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={scrollToDepartures}
        sx={{ mb: 2, ...bookButtonSx }}
      >
        Забронировать
      </Button>

      <Card sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Описание
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {tour.description || 'Описание отсутствует.'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Стоимость: {tour.price != null ? `${Number(tour.price).toLocaleString('ru-RU')} ₽ / место` : 'по запросу'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Предоплата: {tour.prepaymentPercent != null ? `${tour.prepaymentPercent}%` : 'не указана'}
          </Typography>
        </CardContent>
      </Card>

      <Card ref={departuresRef} sx={{ borderRadius: 2, scrollMarginTop: { xs: 72, sm: 88 } }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Даты заездов
          </Typography>
          {Array.isArray(tour.periods) && tour.periods.length > 0 ? (
            tour.periods.map((p, idx) => (
              <Box
                key={`${p.dateFrom}-${p.dateTo}-${idx}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.75,
                  borderBottom: idx === tour.periods.length - 1 ? 'none' : '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {formatDate(p.dateFrom)} - {formatDate(p.dateTo)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  мест: {p.seatsAvailable ?? 0}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  disabled={(p.seatsAvailable ?? 0) < 1}
                  onClick={() => handleBookClick(idx)}
                  sx={{ flexShrink: 0, ...bookButtonSx }}
                >
                  Забронировать
                </Button>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              Пока нет опубликованных заездов.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Dialog open={authDialogOpen} onClose={() => setAuthDialogOpen(false)}>
        <DialogTitle sx={{ fontFamily: 'serif' }}>Вход в аккаунт</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Чтобы забронировать тур, войдите или зарегистрируйтесь.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            component={Link}
            to="/register"
            state={{
              redirectTo:
                pendingPeriodIndex != null
                  ? `/tours/${encodeURIComponent(tourNumber)}/book?period=${pendingPeriodIndex}`
                  : `/tours/${encodeURIComponent(tourNumber)}`,
            }}
            onClick={() => setAuthDialogOpen(false)}
            sx={{ textTransform: 'none', color: '#8B7D6B' }}
          >
            Регистрация
          </Button>
          <Button
            component={Link}
            to="/login"
            state={{
              redirectTo:
                pendingPeriodIndex != null
                  ? `/tours/${encodeURIComponent(tourNumber)}/book?period=${pendingPeriodIndex}`
                  : `/tours/${encodeURIComponent(tourNumber)}`,
            }}
            onClick={() => setAuthDialogOpen(false)}
            variant="contained"
            sx={{ textTransform: 'none', backgroundColor: '#8B7D6B', '&:hover': { backgroundColor: '#6B5D4B' } }}
          >
            Войти
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={profileDialogOpen}
        onClose={() => !profileSaving && setProfileDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { backgroundColor: '#F5F3F0' } }}
      >
        <DialogTitle sx={{ fontFamily: 'serif' }}>Данные для бронирования</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {profileError && (
            <Alert severity="error" onClose={() => setProfileError('')}>
              {profileError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            Укажите недостающие поля — они нужны для оформления брони.
          </Typography>
          {profileGaps.includes('fullName') && (
            <TextField
              label="ФИО"
              value={profileDraft.fullName}
              onChange={(e) => setProfileDraft((d) => ({ ...d, fullName: e.target.value }))}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
            />
          )}
          {profileGaps.includes('passportSeries') && (
            <TextField
              label="Серия паспорта"
              value={profileDraft.passportSeries}
              onChange={(e) => setProfileDraft((d) => ({ ...d, passportSeries: e.target.value }))}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
            />
          )}
          {profileGaps.includes('passportNumber') && (
            <TextField
              label="Номер паспорта"
              value={profileDraft.passportNumber}
              onChange={(e) => setProfileDraft((d) => ({ ...d, passportNumber: e.target.value }))}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
            />
          )}
          {profileGaps.includes('phoneNumber') && (
            <TextField
              label="Телефон"
              value={profileDraft.phoneNumber}
              onChange={(e) => setProfileDraft((d) => ({ ...d, phoneNumber: e.target.value }))}
              fullWidth
              required
              placeholder="+79991234567"
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setProfileDialogOpen(false);
              setPendingPeriodIndex(null);
            }}
            disabled={profileSaving}
            sx={{ textTransform: 'none', color: '#8B7D6B' }}
          >
            Отмена
          </Button>
          <Button
            onClick={handleSaveProfileAndBook}
            disabled={profileSaving}
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: '#8B7D6B',
              '&:hover': { backgroundColor: '#6B5D4B' },
            }}
          >
            {profileSaving ? 'Сохранение…' : 'Сохранить и продолжить'}
          </Button>
        </DialogActions>
      </Dialog>

      {viewerOpen && (
        <Lightbox
          slides={lightboxSlides}
          open={viewerOpen}
          index={activePhotoIndex}
          close={closeViewer}
          onIndexChange={setActivePhotoIndex}
        />
      )}
    </Container>
  );
};

export default TourPage;
